import React, { useEffect, useState } from 'react';
import { ClockIcon, CalendarIcon, MapPinIcon, TruckIcon, InfoIcon, DollarSignIcon, TargetIcon, ZapIcon, UserIcon, MapIcon, PlusIcon, SearchIcon } from 'lucide-react';
// Location search component for brokers
const LocationSearch = ({
  label,
  value,
  onChange,
  placeholder,
  existingLocations
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  existingLocations: string[];
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value);
  const [filteredLocations, setFilteredLocations] = useState(existingLocations);
  useEffect(() => {
    setSearchTerm(value);
  }, [value]);
  useEffect(() => {
    const filtered = existingLocations.filter(location => location.toLowerCase().includes(searchTerm.toLowerCase()));
    setFilteredLocations(filtered);
  }, [searchTerm, existingLocations]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    onChange(newValue);
    setIsOpen(true);
  };
  const handleSelectLocation = (location: string) => {
    setSearchTerm(location);
    onChange(location);
    setIsOpen(false);
  };
  const handleAddNewLocation = () => {
    if (searchTerm && !existingLocations.includes(searchTerm)) {
      onChange(searchTerm);
      setIsOpen(false);
    }
  };
  const showAddNewOption = searchTerm && !existingLocations.some(loc => loc.toLowerCase() === searchTerm.toLowerCase());
  return <div className="relative" data-id="element-1551">
      <label className="flex items-center text-sm font-medium mb-1" data-id="element-1552">
        <MapPinIcon size={16} className="mr-1" data-id="element-1553" />
        {label}
      </label>
      <div className="relative" data-id="element-1554">
        <input type="text" value={searchTerm} onChange={handleInputChange} onFocus={() => setIsOpen(true)} onBlur={() => setTimeout(() => setIsOpen(false), 200)} className="w-full border border-gray-300 rounded-md p-2 pr-8" placeholder={placeholder} required data-id="element-1555" />
        <SearchIcon size={16} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-1556" />
      </div>
      {isOpen && (filteredLocations.length > 0 || showAddNewOption) && <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto" data-id="element-1557">
          {filteredLocations.map((location, index) => <button key={index} type="button" className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2" onClick={() => handleSelectLocation(location)} data-id="element-1558">
              <MapPinIcon size={14} className="text-gray-400" data-id="element-1559" />
              {location}
            </button>)}
          {showAddNewOption && <button type="button" className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 text-blue-600 border-t" onClick={handleAddNewLocation} data-id="element-1560">
              <PlusIcon size={14} data-id="element-1561" />
              Add "{searchTerm}" as new location
            </button>}
        </div>}
    </div>;
};
interface CreateJobFormProps {
  onCancel: () => void;
  onSubmit: (jobData: any) => void;
  prefilledData?: any;
  userRole?: string;
}
export const CreateJobForm = ({
  onCancel,
  onSubmit,
  prefilledData,
  userRole
}: CreateJobFormProps) => {
  const [jobData, setJobData] = useState({
    jobName: '',
    jobNumber: '',
    date: '',
    startTime: '',
    material: '',
    numberOfTrucks: '1 truck',
    truckType: '',
    loadLocation: '',
    dumpLocation: '',
    foreman: '',
    foremanPhone: '',
    foremanLocation: '',
    foremanLocationMethod: 'address',
    contractorName: '',
    customer: '',
    rateType: 'hourly',
    hourlyRate: '',
    tonnageRate: '',
    targetLoads: '',
    targetTonnage: '',
    travelTime: '',
    comments: '',
    assignmentType: 'undecided',
    geoPunchRequired: true,
    geoPunchDistance: 150
  });
  // Sample existing locations for brokers
  const existingLocations = ['Downtown SLC Project - 123 Main St, Salt Lake City, UT 84101', 'Sugar House Development - 2100 S 1100 E, Salt Lake City, UT 84106', 'West Valley Materials Yard - 3500 W 4500 S, West Valley City, UT 84119', 'Granite East Quarry - 4500 E 6200 S, Granite, UT 84092', 'Sand Pit North - 1200 N Beck St, Salt Lake City, UT 84116', 'Highway 89 Site - Mile Marker 15, Highway 89, UT', 'Woods Cross Hi Road Base - 2550 S 800 W, Woods Cross, UT 84087', 'Airport Construction Zone - 776 N Terminal Dr, Salt Lake City, UT 84122', 'University Campus Site - 201 Presidents Cir, Salt Lake City, UT 84112', 'Industrial Park East - 5600 W 4100 S, Salt Lake City, UT 84118'];
  // Populate form with prefilled data when provided
  useEffect(() => {
    if (prefilledData) {
      setJobData(prefilledData);
    }
  }, [prefilledData]);
  // Sample data for random generation
  const sampleJobNames = ['Downtown Commercial Complex Phase 1', 'Highway 89 Expansion Project', 'Residential Development Phase 2', 'Industrial Park Infrastructure', 'Shopping Center Renovation', 'Airport Terminal Extension', 'University Campus Expansion', 'Medical Center Construction', 'Bridge Reconstruction Project', 'Warehouse District Development'];
  const sampleMaterials = ['Gravel', 'Sand', 'Concrete', 'Asphalt', 'Fill Dirt', 'Topsoil', 'Crushed Rock', 'Base Course', 'Road Base', 'Aggregate'];
  const truckTypes = ['dump truck 10', 'dump truck 12', 'dump truck 14+', 'strong arm 12', 'strong arm 14', 'strong arm 16+', 'side dump', 'super side 30tn+', 'jumbo side 35tn+', 'belly dump', 'double belly'];
  const locations = ['Downtown SLC Project', 'Sugar House Development', 'West Valley Materials Yard'];
  const foremen = ['John Smith', 'Mike Johnson', 'Sarah Williams'];
  const truckCounts = ['1 truck', '2 trucks', '3 trucks', '4 trucks', '5 trucks', '6+ trucks'];
  const sampleComments = ['Priority job - start early', 'Weather dependent - check conditions', 'Coordinate with site supervisor', 'Heavy traffic area - use caution', 'Multiple deliveries required', 'Site access limited - call ahead'];
  const generateRandomJob = () => {
    // Generate random date within next 30 days
    const today = new Date();
    const randomDays = Math.floor(Math.random() * 30);
    const randomDate = new Date(today.getTime() + randomDays * 24 * 60 * 60 * 1000);
    const formattedDate = randomDate.toISOString().split('T')[0];
    // Generate random start time between 6 AM and 3 PM
    const startHour = 6 + Math.floor(Math.random() * 10);
    const startMinute = Math.random() < 0.5 ? '00' : '30';
    const formattedTime = `${startHour.toString().padStart(2, '0')}:${startMinute}`;
    // Generate random job number
    const jobNumber = `JOB-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 9999) + 1).padStart(4, '0')}`;
    // Generate random rates
    const hourlyRate = (85 + Math.random() * 40).toFixed(2); // $85-125/hr
    const tonnageRate = (15 + Math.random() * 10).toFixed(2); // $15-25/ton
    // Generate random targets
    const targetLoads = Math.floor(Math.random() * 20) + 5; // 5-25 loads
    const targetTonnage = Math.floor(Math.random() * 400) + 100; // 100-500 tons
    // Generate random travel time (15-90 minutes)
    const travelTime = Math.floor(Math.random() * 76) + 15; // 15-90 minutes
    // Sample customers for brokers
    const sampleCustomers = ['ABC Construction Company', 'Metro Development Corp', 'Sunrise Building Group', 'Valley Construction LLC', 'Premier Contractors Inc', 'Mountain West Builders', 'Urban Development Partners', 'Skyline Construction Co', 'Gateway Building Solutions', 'Pinnacle Development Group'];
    const randomData = {
      jobName: sampleJobNames[Math.floor(Math.random() * sampleJobNames.length)],
      jobNumber: jobNumber,
      date: formattedDate,
      startTime: formattedTime,
      material: sampleMaterials[Math.floor(Math.random() * sampleMaterials.length)],
      numberOfTrucks: truckCounts[Math.floor(Math.random() * truckCounts.length)],
      truckType: truckTypes[Math.floor(Math.random() * truckTypes.length)],
      loadLocation: userRole === 'broker' ? existingLocations[Math.floor(Math.random() * existingLocations.length)] : locations[Math.floor(Math.random() * locations.length)],
      dumpLocation: userRole === 'broker' ? existingLocations[Math.floor(Math.random() * existingLocations.length)] : locations[Math.floor(Math.random() * locations.length)],
      foreman: foremen[Math.floor(Math.random() * foremen.length)],
      customer: userRole === 'broker' ? sampleCustomers[Math.floor(Math.random() * sampleCustomers.length)] : '',
      rateType: Math.random() < 0.6 ? 'hourly' : 'tonnage',
      hourlyRate: hourlyRate,
      tonnageRate: tonnageRate,
      targetLoads: targetLoads.toString(),
      targetTonnage: targetTonnage.toString(),
      travelTime: travelTime.toString(),
      comments: Math.random() < 0.7 ? sampleComments[Math.floor(Math.random() * sampleComments.length)] : '',
      sendToBroker: Math.random() < 0.3,
      geoPunchRequired: true,
      geoPunchDistance: Math.floor(Math.random() * 350) + 150 // Random distance between 150-500 feet
    };
    setJobData(randomData);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    if (type === 'checkbox') {
      setJobData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setJobData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  const handleRateTypeChange = (type: string) => {
    setJobData(prev => ({
      ...prev,
      rateType: type
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(jobData);
  };
  return <div className="bg-white rounded-lg shadow-md p-6 max-w-6xl mx-auto" data-id="element-1562">
      <div className="flex justify-between items-center mb-6" data-id="element-1563">
        <div data-id="element-1564">
          <h1 className="text-2xl font-bold" data-id="element-1565">
            {prefilledData ? 'Create Job from Request' : 'Create New Job'}
          </h1>
          {prefilledData && <p className="text-gray-600 mt-1" data-id="element-1566">
              This job is based on a foreman request and has been pre-populated
              with the requested details.
            </p>}
        </div>
        {!prefilledData && <button onClick={generateRandomJob} className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md flex items-center gap-2" data-id="element-1567">
            <ZapIcon size={18} data-id="element-1568" />
            Generate a Random Job
          </button>}
      </div>
      <form onSubmit={handleSubmit} data-id="element-1569">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1570">
          {/* Left Column - Job Information */}
          <div data-id="element-1571">
            <h2 className="text-lg font-medium mb-4" data-id="element-1572">Job Information</h2>
            {/* Customer field - only show for brokers */}
            {userRole === 'broker' && <div className="mb-4" data-id="element-1573">
                <label className="block text-sm font-medium mb-1" data-id="element-1574">
                  Customer
                </label>
                <input type="text" name="customer" value={jobData.customer} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" placeholder="Customer name" required data-id="element-1575" />
              </div>}
            <div className="mb-4" data-id="element-1576">
              <label className="block text-sm font-medium mb-1" data-id="element-1577">Job Name</label>
              <input type="text" name="jobName" value={jobData.jobName} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" required data-id="element-1578" />
            </div>
            <div className="mb-4" data-id="element-1579">
              <label className="block text-sm font-medium mb-1" data-id="element-1580">
                Job Number
              </label>
              <input type="text" name="jobNumber" value={jobData.jobNumber} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" required data-id="element-1581" />
            </div>
            <div className="mb-4" data-id="element-1582">
              <label className="flex items-center text-sm font-medium mb-1" data-id="element-1583">
                <CalendarIcon size={16} className="mr-1" data-id="element-1584" />
                Date
              </label>
              <input type="date" name="date" value={jobData.date} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" required data-id="element-1585" />
            </div>
            <div className="mb-4" data-id="element-1586">
              <label className="flex items-center text-sm font-medium mb-1" data-id="element-1587">
                <ClockIcon size={16} className="mr-1" data-id="element-1588" />
                Start Time
              </label>
              <input type="time" name="startTime" value={jobData.startTime} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" required data-id="element-1589" />
              <p className="text-xs text-gray-500 mt-1" data-id="element-1590">
                Expected job start time
              </p>
            </div>
            <div className="mb-4" data-id="element-1591">
              <label className="block text-sm font-medium mb-1" data-id="element-1592">Material</label>
              <input type="text" name="material" value={jobData.material} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" required data-id="element-1593" />
            </div>
            <div className="mb-4" data-id="element-1594">
              <label className="flex items-center text-sm font-medium mb-1" data-id="element-1595">
                <TruckIcon size={16} className="mr-1" data-id="element-1596" />
                Truck Type Required
              </label>
              <select name="truckType" value={jobData.truckType} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" required data-id="element-1597">
                <option value="" data-id="element-1598">Select truck type</option>
                <option value="dump truck 10" data-id="element-1599">Dump Truck 10</option>
                <option value="dump truck 12" data-id="element-1600">Dump Truck 12</option>
                <option value="dump truck 14+" data-id="element-1601">Dump Truck 14+</option>
                <option value="strong arm 12" data-id="element-1602">Strong Arm 12</option>
                <option value="strong arm 14" data-id="element-1603">Strong Arm 14</option>
                <option value="strong arm 16+" data-id="element-1604">Strong Arm 16+</option>
                <option value="side dump" data-id="element-1605">Side Dump</option>
                <option value="super side 30tn+" data-id="element-1606">Super Side 30tn+</option>
                <option value="jumbo side 35tn+" data-id="element-1607">Jumbo Side 35tn+</option>
                <option value="belly dump" data-id="element-1608">Belly Dump</option>
                <option value="double belly" data-id="element-1609">Double Belly</option>
              </select>
            </div>
            <div className="mb-4" data-id="element-1610">
              <label className="flex items-center text-sm font-medium mb-1" data-id="element-1611">
                <TruckIcon size={16} className="mr-1" data-id="element-1612" />
                Number of Trucks Requested
              </label>
              <select name="numberOfTrucks" value={jobData.numberOfTrucks} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" required data-id="element-1613">
                <option value="1 truck" data-id="element-1614">1 truck</option>
                <option value="2 trucks" data-id="element-1615">2 trucks</option>
                <option value="3 trucks" data-id="element-1616">3 trucks</option>
                <option value="4 trucks" data-id="element-1617">4 trucks</option>
                <option value="5 trucks" data-id="element-1618">5 trucks</option>
                <option value="6+ trucks" data-id="element-1619">6+ trucks</option>
              </select>
            </div>
            <div className="mb-4" data-id="element-1620">
              <label className="flex items-center text-sm font-medium mb-1" data-id="element-1621">
                <ClockIcon size={16} className="mr-1" data-id="element-1622" />
                Travel Time
                <span className="text-xs text-gray-500 ml-2" data-id="element-1623">(Optional)</span>
              </label>
              <div className="flex items-center" data-id="element-1624">
                <input type="number" name="travelTime" value={jobData.travelTime} onChange={handleChange} placeholder="0" min="0" max="999" className="w-full border border-gray-300 rounded-r-0 rounded-md p-2" data-id="element-1625" />
                <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md text-sm" data-id="element-1626">
                  minutes
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1" data-id="element-1627">
                Estimated travel time between load and dump locations. Critical
                for job scheduling and driver assignments.
              </p>
            </div>
          </div>
          {/* Right Column - Location & Assignment */}
          <div data-id="element-1628">
            <h2 className="text-lg font-medium mb-4" data-id="element-1629">Location & Assignment</h2>
            <div className="mb-4" data-id="element-1630">
              {userRole === 'broker' ? <LocationSearch label="Load Location" value={jobData.loadLocation} onChange={value => setJobData(prev => ({
              ...prev,
              loadLocation: value
            }))} placeholder="Search or enter load location" existingLocations={existingLocations} data-id="element-1631" /> : <>
                  <label className="flex items-center text-sm font-medium mb-1" data-id="element-1632">
                    <MapPinIcon size={16} className="mr-1" data-id="element-1633" />
                    Load Location
                  </label>
                  <select name="loadLocation" value={jobData.loadLocation} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" required data-id="element-1634">
                    <option value="" data-id="element-1635">Select a load location</option>
                    <option value="Downtown SLC Project" data-id="element-1636">
                      Downtown SLC Project
                    </option>
                    <option value="Sugar House Development" data-id="element-1637">
                      Sugar House Development
                    </option>
                    <option value="West Valley Materials Yard" data-id="element-1638">
                      West Valley Materials Yard
                    </option>
                  </select>
                </>}
            </div>
            <div className="mb-4" data-id="element-1639">
              {userRole === 'broker' ? <LocationSearch label="Dump Location" value={jobData.dumpLocation} onChange={value => setJobData(prev => ({
              ...prev,
              dumpLocation: value
            }))} placeholder="Search or enter dump location" existingLocations={existingLocations} data-id="element-1640" /> : <>
                  <label className="flex items-center text-sm font-medium mb-1" data-id="element-1641">
                    <MapPinIcon size={16} className="mr-1" data-id="element-1642" />
                    Dump Location
                  </label>
                  <select name="dumpLocation" value={jobData.dumpLocation} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" required data-id="element-1643">
                    <option value="" data-id="element-1644">Select a dump location</option>
                    <option value="Downtown SLC Project" data-id="element-1645">
                      Downtown SLC Project
                    </option>
                    <option value="Sugar House Development" data-id="element-1646">
                      Sugar House Development
                    </option>
                    <option value="West Valley Materials Yard" data-id="element-1647">
                      West Valley Materials Yard
                    </option>
                  </select>
                </>}
            </div>
            {/* Geo Punch Requirements - Show for both broker and contractor */}
            <div className="mb-4 p-4 bg-blue-50 rounded-md" data-id="element-1648">
              <h3 className="flex items-center text-sm font-medium mb-3" data-id="element-1649">
                <MapPinIcon size={16} className="mr-1" data-id="element-1650" />
                Geo Punch Requirements
              </h3>
              <div className="mb-3" data-id="element-1651">
                <label className="flex items-center" data-id="element-1652">
                  <input type="checkbox" name="geoPunchRequired" checked={jobData.geoPunchRequired} onChange={handleChange} className="mr-2" data-id="element-1653" />
                  Require drivers to be at location to clock in/out
                </label>
                <p className="text-xs text-gray-600 mt-1 ml-5" data-id="element-1654">
                  Drivers must be physically present at job locations to record
                  time
                </p>
              </div>
              {jobData.geoPunchRequired && <div data-id="element-1655">
                  <label className="block text-sm mb-1" data-id="element-1656">
                    Maximum Distance from Location Pin
                  </label>
                  <div className="flex items-center" data-id="element-1657">
                    <input type="range" name="geoPunchDistance" min="50" max="500" step="10" value={jobData.geoPunchDistance} onChange={handleChange} className="w-full mr-3" data-id="element-1658" />
                    <div className="flex items-center" data-id="element-1659">
                      <input type="number" name="geoPunchDistance" value={jobData.geoPunchDistance} onChange={handleChange} min="50" max="500" className="w-16 border border-gray-300 rounded-l-md p-2 text-center" data-id="element-1660" />
                      <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md text-sm" data-id="element-1661">
                        feet
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-1662">
                    <span data-id="element-1663">50 ft (Strict)</span>
                    <span data-id="element-1664">150 ft (Default)</span>
                    <span data-id="element-1665">500 ft (Lenient)</span>
                  </div>
                </div>}
            </div>
            <div className="mb-4" data-id="element-1666">
              <label className="flex items-center text-sm font-medium mb-1" data-id="element-1667">
                <UserIcon size={16} className="mr-1" data-id="element-1668" />
                Foreman
                <InfoIcon size={14} className="ml-1 text-gray-500" data-id="element-1669" />
              </label>
              {userRole === 'broker' ? <div className="space-y-3" data-id="element-1670">
                  <input type="text" name="foreman" value={jobData.foreman} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" placeholder="Foreman name" required data-id="element-1671" />
                  <input type="tel" name="foremanPhone" value={jobData.foremanPhone} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" placeholder="Foreman phone number (optional)" data-id="element-1672" />
                </div> : <>
                  <select name="foreman" value={jobData.foreman} onChange={handleChange} className="w-full border border-gray-300 rounded-md p-2" required data-id="element-1673">
                    <option value="" data-id="element-1674">Select a foreman</option>
                    <option value="John Smith" data-id="element-1675">John Smith</option>
                    <option value="Mike Johnson" data-id="element-1676">Mike Johnson</option>
                    <option value="Sarah Williams" data-id="element-1677">Sarah Williams</option>
                  </select>
                  <div className="mt-1" data-id="element-1678">
                    <p className="text-xs text-gray-500 flex items-center" data-id="element-1679">
                      <InfoIcon size={12} className="mr-1" data-id="element-1680" />
                      Select a foreman to set phone number visibility.
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1681">
                      All other messages are handled within the system without
                      sharing personal phone numbers.
                    </p>
                  </div>
                </>}
            </div>
            {/* Rate Information */}
            <div className="mb-4 p-4 bg-blue-50 rounded-md" data-id="element-1682">
              <h3 className="flex items-center text-sm font-medium mb-3" data-id="element-1683">
                <DollarSignIcon size={16} className="mr-1" data-id="element-1684" />
                Rate Information
                <span className="text-xs text-gray-500 ml-2" data-id="element-1685">(Optional)</span>
              </h3>
              <div className="flex items-center space-x-4 mb-3" data-id="element-1686">
                <label className="flex items-center" data-id="element-1687">
                  <input type="radio" name="rateType" checked={jobData.rateType === 'hourly'} onChange={() => handleRateTypeChange('hourly')} className="mr-2" data-id="element-1688" />
                  <ClockIcon size={16} className="mr-1" data-id="element-1689" />
                  Hourly Rate
                </label>
                <label className="flex items-center" data-id="element-1690">
                  <input type="radio" name="rateType" checked={jobData.rateType === 'tonnage'} onChange={() => handleRateTypeChange('tonnage')} className="mr-2" data-id="element-1691" />
                  <TruckIcon size={16} className="mr-1" data-id="element-1692" />
                  Tonnage Rate
                </label>
              </div>
              {jobData.rateType === 'hourly' && <div data-id="element-1693">
                  <label className="block text-sm mb-1" data-id="element-1694">
                    Hourly Rate ($)
                    <span className="text-xs text-gray-500 ml-2" data-id="element-1695">
                      (Optional)
                    </span>
                  </label>
                  <div className="flex items-center" data-id="element-1696">
                    <span className="px-3 py-2 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md" data-id="element-1697">
                      $
                    </span>
                    <input type="number" name="hourlyRate" value={jobData.hourlyRate} onChange={handleChange} placeholder="0.00 per hour" className="w-full border border-gray-300 rounded-r-md p-2" data-id="element-1698" />
                  </div>
                </div>}
              {jobData.rateType === 'tonnage' && <div data-id="element-1699">
                  <label className="block text-sm mb-1" data-id="element-1700">
                    Tonnage Rate ($)
                    <span className="text-xs text-gray-500 ml-2" data-id="element-1701">
                      (Optional)
                    </span>
                  </label>
                  <div className="flex items-center" data-id="element-1702">
                    <span className="px-3 py-2 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md" data-id="element-1703">
                      $
                    </span>
                    <input type="number" name="tonnageRate" value={jobData.tonnageRate} onChange={handleChange} placeholder="0.00 per ton" className="w-full border border-gray-300 rounded-r-md p-2" data-id="element-1704" />
                  </div>
                </div>}
            </div>
            {/* Job Goals */}
            <div className="mb-4 p-4 bg-blue-50 rounded-md" data-id="element-1705">
              <h3 className="flex items-center text-sm font-medium mb-3" data-id="element-1706">
                <TargetIcon size={16} className="mr-1" data-id="element-1707" />
                Job Goals
                <span className="text-xs text-gray-500 ml-2" data-id="element-1708">(Optional)</span>
              </h3>
              <div className="grid grid-cols-2 gap-4" data-id="element-1709">
                <div data-id="element-1710">
                  <label className="block text-sm mb-1" data-id="element-1711">
                    Target Number of Loads
                  </label>
                  <input type="number" name="targetLoads" value={jobData.targetLoads} onChange={handleChange} placeholder="e.g., 10" className="w-full border border-gray-300 rounded-md p-2" data-id="element-1712" />
                </div>
                <div data-id="element-1713">
                  <label className="block text-sm mb-1" data-id="element-1714">Target Tonnage</label>
                  <input type="number" name="targetTonnage" value={jobData.targetTonnage} onChange={handleChange} placeholder="e.g., 500.00" className="w-full border border-gray-300 rounded-md p-2" data-id="element-1715" />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Comments */}
        <div className="mb-6" data-id="element-1716">
          <label className="block text-sm font-medium mb-1" data-id="element-1717">Comments</label>
          <textarea name="comments" value={jobData.comments} onChange={handleChange} rows={4} className="w-full border border-gray-300 rounded-md p-2" data-id="element-1718"></textarea>
        </div>
        {/* Broker Assignment - Only show for contractors */}
        {userRole !== 'broker' && <div className="mb-6 p-4 bg-gray-50 border rounded-md" data-id="element-1719">
            <h3 className="flex items-center text-sm font-medium mb-3" data-id="element-1720">
              <TruckIcon size={16} className="mr-1" data-id="element-1721" />
              Assignment Method
            </h3>
            <div className="mb-3 space-y-2" data-id="element-1722">
              <label className="flex items-center" data-id="element-1723">
                <input type="radio" name="assignmentType" value="undecided" checked={jobData.assignmentType === 'undecided'} onChange={e => setJobData(prev => ({
              ...prev,
              assignmentType: 'undecided'
            }))} className="mr-2" data-id="element-1724" />
                Decide later (job will appear in both assignment tabs)
              </label>
              <label className="flex items-center" data-id="element-1725">
                <input type="radio" name="assignmentType" value="broker" checked={jobData.assignmentType === 'broker'} onChange={e => setJobData(prev => ({
              ...prev,
              assignmentType: 'broker'
            }))} className="mr-2" data-id="element-1726" />
                Assign to brokers (job will only appear in broker assignment
                tab)
              </label>
              <label className="flex items-center" data-id="element-1727">
                <input type="radio" name="assignmentType" value="direct" checked={jobData.assignmentType === 'direct'} onChange={e => setJobData(prev => ({
              ...prev,
              assignmentType: 'direct'
            }))} className="mr-2" data-id="element-1728" />
                Assign directly to trucking companies (job will only appear in
                direct assignment tab)
              </label>
            </div>
            <div className="flex items-start gap-2 text-sm text-gray-600 bg-blue-50 p-3 rounded-md" data-id="element-1729">
              <InfoIcon size={18} className="text-blue-500 flex-shrink-0 mt-0.5" data-id="element-1730" />
              <div data-id="element-1731">
                <p data-id="element-1732">
                  Select an assignment method to prevent duplicate dispatches.
                  Job will be created with "pending" status.
                </p>
                <p className="text-xs mt-1" data-id="element-1733">
                  You'll be taken to the appropriate assignment tab after job
                  creation.
                </p>
              </div>
            </div>
          </div>}
        {/* Form Actions */}
        <div className="flex justify-end gap-4 pt-4 border-t" data-id="element-1734">
          <button type="button" onClick={onCancel} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50" data-id="element-1735">
            Cancel
          </button>
          <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-1736">
            Create Job
          </button>
        </div>
      </form>
    </div>;
};